import type { FC, ReactNode } from 'react'
import type { WorkBook } from 'xlsx'
import type { TabId } from '@/constants/tabs'
import type { Filters } from '@/lib/filters'
import type { EditLogEntry } from '@/lib/data/export'

import type {
    Agreement,
    BasicControl,
    BudgetRow,
    ComplianceItem,
    CsfItem,
    Dataset,
    DesignActivity,
    Dims,
    Finding,
    Risk,
    SheetKey,
    TierAsset,
    VolumetryRow
} from '@/lib/data/types'

import type {
    BudgetEdit,
    CurrencyContext,
    FilterOptions,
    SnapItem,
    Thresholds
} from '@/lib/metrics/core'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { flushSync } from 'react-dom'
import { loadWorkbook } from '@/lib/data/parse'
import { EMPTY_FILTERS } from '@/lib/filters'

import {
    applyBudgetEdits,
    buildSnapshots,
    createMatcher,
    currencyContext,
    cutOptions,
    filterOptions,
    thresholdsOf
} from '@/lib/metrics/core'

/* ------------------------------------------------------------------ */
/* Derived data: everything the tabs render, already filtered          */
/* ------------------------------------------------------------------ */

export interface Derived {
    ds: Dataset
    th: Thresholds
    cuts: number[]
    ref: number
    prev: number | null
    isLatest: boolean
    /** Portfolio at the reference date */
    snap: SnapItem[]
    /** Portfolio at the previous cut, when there is one */
    prevSnap: SnapItem[] | null
    /** Portfolio at every cut up to the reference date, ascending */
    series: { cut: number, items: SnapItem[] }[]
    compliance: ComplianceItem[]
    csf: CsfItem[]
    design: DesignActivity[]
    findings: Finding[]
    volumetry: VolumetryRow[]
    tiers: TierAsset[]
    risks: Risk[]
    budget: BudgetRow[]
    agreements: Agreement[]
    basics: BasicControl[]
    currency: CurrencyContext
    /** True when the sheet was recognized and has at least one valid row */
    has: Record<SheetKey, boolean>
}

type LoadStatus = 'idle' | 'loading' | 'ready'

interface DataValue {
    status: LoadStatus
    error: boolean
    dataset: Dataset | null
    workbook: WorkBook | null
    derived: Derived | null
    options: FilterOptions | null
    filters: Filters
    setFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void
    resetFilters: () => void
    currency: 'MXN' | 'USD' | null
    setCurrency: (currency: 'MXN' | 'USD') => void
    budgetEdits: Record<number, BudgetEdit>
    editLog: EditLogEntry[]
    editBudget: (row: BudgetRow, values: BudgetEdit, periodLabel: string) => void
    discardBudgetEdits: () => void
    loadFile: (file: File) => Promise<boolean>
    unload: () => void
    clearError: () => void
}

interface UiValue {
    tab: TabId
    setTab: (tab: TabId) => void
    isOpen: (id: string) => boolean
    toggleSection: (id: string) => void
    setSections: (ids: string[], open: boolean) => void
    printing: boolean
    print: () => void
}

const DataContext = createContext<DataValue | null>(null)
const UiContext = createContext<UiValue | null>(null)

const SHEET_KEYS: SheetKey[] = ['portfolio', 'history', 'compliance', 'csf', 'design', 'findings', 'volumetry', 'tiers', 'risks', 'budget', 'agreements', 'basics', 'params']

const derive = (
    ds: Dataset,
    filters: Filters,
    snapshots: Map<number, SnapItem[]>,
    cuts: number[],
    th: Thresholds,
    edits: Record<number, BudgetEdit>,
    currency: 'MXN' | 'USD' | null
): Derived => {
    const latest = cuts[cuts.length - 1]
    const ref = filters.cut !== null && cuts.includes(filters.cut) ? filters.cut : latest
    const refIndex = cuts.indexOf(ref)
    const prev = refIndex > 0 ? cuts[refIndex - 1] : null
    const match = createMatcher(ds, filters)

    const keep = (item: SnapItem) => match.dims(item, 'portfolio') && match.range(item.start, item.end, 'portfolio', ['start', 'end'])
    const series = cuts.slice(0, refIndex + 1).map(cut => ({ cut, items: (snapshots.get(cut) ?? []).filter(keep) }))
    const asDims = (item: object) => item as Partial<Dims>

    const has = Object.fromEntries(SHEET_KEYS.map(key => {
        const report = ds.quality.sheets.find(sheet => sheet.key === key)

        return [key, Boolean(report?.recognized && report.rowsValid > 0)]
    })) as Record<SheetKey, boolean>

    return {
        ds,
        th,
        cuts,
        ref,
        prev,
        isLatest: ref === latest,
        snap: series[series.length - 1]?.items ?? [],
        prevSnap: prev !== null ? series[series.length - 2]?.items ?? null : null,
        series,
        compliance: ds.compliance.filter(item => match.dims(item, 'compliance') && match.range(item.due, item.due, 'compliance', ['due'])),
        csf: ds.csf.filter(item => match.dims(asDims(item), 'csf') && match.period(item.period, 'csf', 'period')),
        design: ds.design.filter(item => match.dims(item, 'design') && match.range(item.start, item.end, 'design', ['start', 'end'])),
        findings: ds.findings.filter(item => match.dims(item, 'findings') && match.range(item.detected, item.detected, 'findings', ['detected'])),
        volumetry: ds.volumetry.filter(item => match.dims(asDims(item), 'volumetry') && match.period(item.period, 'volumetry', 'period')),
        tiers: ds.tiers.filter(item => match.dims(item, 'tiers')),
        risks: ds.risks.filter(item => match.dims(item, 'risks')),
        budget: applyBudgetEdits(ds.budget, edits).filter(item => match.dims(item, 'budget') && match.period(item.period, 'budget', 'period')),
        agreements: ds.agreements.filter(item => match.dims(item, 'agreements') && match.range(item.due ?? item.date, item.due ?? item.date, 'agreements', ['due', 'date'])),
        basics: ds.basics.filter(item => match.dims(asDims(item), 'basics') && match.period(item.period, 'basics', 'period')),
        currency: currencyContext(ds, currency),
        has
    }
}

interface DashboardProviderProps {
    children: ReactNode
}

export const DashboardProvider: FC<DashboardProviderProps> = ({ children }): ReactNode => {
    const [status, setStatus] = useState<LoadStatus>('idle')
    const [error, setError] = useState(false)
    const [dataset, setDataset] = useState<Dataset | null>(null)
    const [workbook, setWorkbook] = useState<WorkBook | null>(null)
    const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
    const [currency, setCurrencyState] = useState<'MXN' | 'USD' | null>(null)
    const [budgetEdits, setBudgetEdits] = useState<Record<number, BudgetEdit>>({})
    const [editLog, setEditLog] = useState<EditLogEntry[]>([])
    const [tab, setTabState] = useState<TabId>('ctso')
    const [sections, setSectionState] = useState<Record<string, boolean>>({})
    const [printing, setPrinting] = useState(false)
    const loadToken = useRef(0)

    /* -------------------------- loading -------------------------- */

    const loadFile = useCallback(async (file: File): Promise<boolean> => {
        const token = ++loadToken.current

        setStatus(previous => (previous === 'ready' ? 'ready' : 'loading'))
        setError(false)

        // Let the loading state paint before the synchronous parse starts
        await new Promise(resolve => setTimeout(resolve, 40))

        const result = await loadWorkbook(file)

        if (token !== loadToken.current) return false

        if (!result.ok) {
            setError(true)
            setStatus(previous => (previous === 'loading' ? 'idle' : previous))

            return false
        }

        setDataset(result.dataset)
        setWorkbook(result.workbook)
        setFilters(EMPTY_FILTERS)
        setBudgetEdits({})
        setEditLog([])
        setCurrencyState(null)
        setSectionState({})
        setStatus('ready')

        return true
    }, [])

    const unload = useCallback(() => {
        loadToken.current++
        setDataset(null)
        setWorkbook(null)
        setFilters(EMPTY_FILTERS)
        setBudgetEdits({})
        setEditLog([])
        setCurrencyState(null)
        setSectionState({})
        setError(false)
        setStatus('idle')
        setTabState('ctso')
    }, [])

    const clearError = useCallback(() => setError(false), [])

    /* -------------------------- filters -------------------------- */

    const setFilter = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
        setFilters(previous => {
            const next = { ...previous, [key]: value }

            // A business unit that does not belong to the new division is cleared
            if (key === 'division' && previous.bu) next.bu = null

            return next
        })
    }, [])

    const resetFilters = useCallback(() => setFilters(EMPTY_FILTERS), [])

    /* -------------------------- budget --------------------------- */

    const setCurrency = useCallback((next: 'MXN' | 'USD') => setCurrencyState(next), [])

    const editsRef = useRef<Record<number, BudgetEdit>>({})

    useEffect(() => {
        editsRef.current = budgetEdits
    }, [budgetEdits])

    const editBudget = useCallback((row: BudgetRow, values: BudgetEdit, periodLabel: string) => {
        const now = Date.now()
        const previous = editsRef.current
        const current = previous[row.row] ?? {}
        const next: BudgetEdit = { ...current }
        const entries: EditLogEntry[] = []

        for (const field of ['plan', 'actual'] as const) {
            const value = values[field]

            if (value === undefined) continue

            // row carries the value currently shown, edits included
            const before = row[field]

            if (before === value) continue

            next[field] = value
            entries.push({ row: row.row, line: row.line, period: periodLabel, field, before, after: value, at: now })
        }

        if (!entries.length) return

        const updated = { ...previous, [row.row]: next }

        editsRef.current = updated
        setBudgetEdits(updated)
        setEditLog(log => [...log, ...entries])
    }, [])

    const discardBudgetEdits = useCallback(() => {
        editsRef.current = {}
        setBudgetEdits({})
        setEditLog([])
    }, [])

    /* -------------------------- derived -------------------------- */

    const base = useMemo(() => {
        if (!dataset) return null

        const th = thresholdsOf(dataset)
        const cuts = cutOptions(dataset)

        return { th, cuts, snapshots: buildSnapshots(dataset, cuts, th), options: filterOptions(dataset) }
    }, [dataset])

    const derived = useMemo(() => {
        if (!dataset || !base) return null

        return derive(dataset, filters, base.snapshots, base.cuts, base.th, budgetEdits, currency)
    }, [dataset, base, filters, budgetEdits, currency])

    /* -------------------------- interface ------------------------ */

    const setTab = useCallback((next: TabId) => {
        setTabState(next)

        if (typeof window !== 'undefined') window.scrollTo({ top: 0 })
    }, [])

    const isOpen = useCallback((id: string) => Boolean(sections[id]), [sections])

    const toggleSection = useCallback((id: string) => {
        setSectionState(previous => ({ ...previous, [id]: !previous[id] }))
    }, [])

    const setSections = useCallback((ids: string[], open: boolean) => {
        setSectionState(previous => {
            const next = { ...previous }

            for (const id of ids) next[id] = open

            return next
        })
    }, [])

    useEffect(() => {
        const before = () => flushSync(() => setPrinting(true))
        const after = () => setPrinting(false)

        window.addEventListener('beforeprint', before)
        window.addEventListener('afterprint', after)

        return () => {
            window.removeEventListener('beforeprint', before)
            window.removeEventListener('afterprint', after)
        }
    }, [])

    const print = useCallback(() => {
        flushSync(() => setPrinting(true))

        // Two frames so charts inside newly opened sections can measure themselves
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                window.print()
            })
        })
    }, [])

    const dataValue = useMemo<DataValue>(() => ({
        status,
        error,
        dataset,
        workbook,
        derived,
        options: base?.options ?? null,
        filters,
        setFilter,
        resetFilters,
        currency,
        setCurrency,
        budgetEdits,
        editLog,
        editBudget,
        discardBudgetEdits,
        loadFile,
        unload,
        clearError
    }), [status, error, dataset, workbook, derived, base, filters, setFilter, resetFilters, currency, setCurrency, budgetEdits, editLog, editBudget, discardBudgetEdits, loadFile, unload, clearError])

    const uiValue = useMemo<UiValue>(() => ({
        tab,
        setTab,
        isOpen,
        toggleSection,
        setSections,
        printing,
        print
    }), [tab, setTab, isOpen, toggleSection, setSections, printing, print])

    return (
        <DataContext.Provider value={dataValue}>
            <UiContext.Provider value={uiValue}>
                {children}
            </UiContext.Provider>
        </DataContext.Provider>
    )
}

export const useDashboard = (): DataValue => {
    const value = useContext(DataContext)

    if (!value) throw new Error('useDashboard must be used inside DashboardProvider')

    return value
}

/** Derived data for tabs. Tabs only render once a workbook is loaded. */
export const useDerived = (): Derived => {
    const { derived } = useDashboard()

    if (!derived) throw new Error('useDerived requires a loaded workbook')

    return derived
}

export const useUi = (): UiValue => {
    const value = useContext(UiContext)

    if (!value) throw new Error('useUi must be used inside DashboardProvider')

    return value
}
